import React, { useEffect, useState } from "react";
import { Check, Copy, Globe2, Linkedin, Mail, MessageCircle, Share2, Twitter } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { getBriefLenses } from "../../utils/api";

/**
 * DNH — Share Brief Dialog.
 * On devices that support the Web Share API (iOS Safari, Android Chrome, etc.)
 * the native share sheet is triggered immediately — no modal needed.
 * On desktop / unsupported browsers it falls back to the custom modal.
 */

const todayKey = () => new Date().toISOString().slice(0, 10);

/** Trigger the OS-native share sheet if available, returns true if used */
export const nativeShare = async ({ title, url }) => {
  if (!navigator.share) return false;
  try {
    await navigator.share({ title, url });
    return true;
  } catch (err) {
    if (err?.name === "AbortError") return true; // user dismissed — that's fine
    return false;
  }
};

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

export const ShareBriefDialog = ({ open, onClose, scope, slug, title }) => {
  const [lens, setLens] = useState("");
  const [lenses, setLenses] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || scope === "story") return;
    setCopied(false);
    getBriefLenses()
      .then((d) => setLenses(d?.lenses || []))
      .catch(() => {});
  }, [open, scope]);

  // Story scope: use the OG-proxy so LinkedIn/WhatsApp/Twitter see real meta tags
  const shareUrl = scope === "story"
    ? `${BASE_URL}/api/brief/open/${slug}`
    : scope === "global"
    ? `${window.location.origin}/brief/global/${todayKey()}${lens ? `?lens=${lens}` : ""}`
    : `${window.location.origin}/brief/${scope}/${slug || "global"}/${todayKey()}${lens ? `?lens=${lens}` : ""}`;

  const lensName = lenses.find((l) => l.slug === lens)?.name;
  const shareText = lens
    ? `${title} — through ${lensName}'s lens · via Daily News Highlights`
    : `${title} · via Daily News Highlights`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Permanent link copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy — select the link manually");
    }
  };

  const networks = [
    {
      id: "x",
      label: "X",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`,
    },
    {
      id: "email",
      label: "Email",
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[var(--paper)] border-[var(--hairline)] border-t-[var(--crimson)] p-0 gap-0 overflow-hidden [border-top-color:var(--crimson)] [border-top-width:4px]"
        style={{ width: "min(460px, calc(100vw - 32px))", maxWidth: "460px" }}
        data-testid="share-brief-dialog"
      >
        {/* Crimson top bar */}
        {/* No inner bar needed - using border-t approach */}

        <div className="px-5 pt-5 pb-5 flex flex-col gap-4 overflow-hidden">
          <DialogHeader className="gap-1 pr-6">
            <DialogTitle className="font-serif text-[20px] leading-tight text-[var(--ink)]">
              Share this brief
            </DialogTitle>
            <DialogDescription className="font-sans text-[12.5px] leading-[1.5] text-[var(--muted)]">
              Share a headline preview. Full analysis is available to subscribers.
            </DialogDescription>
          </DialogHeader>

          {/* Lens picker */}
          {scope !== "story" && <div>
            <div className="flex items-center gap-1.5 font-mono text-[9.5px] tracking-[0.18em] uppercase text-[var(--ink-2)] mb-2">
              <Globe2 size={11} strokeWidth={1.75} />
              Country lens
              <span className="text-[var(--muted)] normal-case tracking-normal font-sans text-[11px]">(optional)</span>
            </div>
            <div
              className="flex flex-wrap gap-1.5 max-h-[96px] overflow-y-auto"
              style={{ overflowX: "hidden" }}
              data-testid="share-lens-picker"
            >
              <button
                type="button"
                onClick={() => setLens("")}
                className={`h-6 px-2.5 rounded-full text-[11px] font-medium border transition-all shrink-0 ${
                  !lens
                    ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
                    : "bg-[var(--paper)] text-[var(--ink-2)] border-[var(--hairline)] hover:border-[var(--ink)]"
                }`}
                data-testid="share-lens-none"
              >
                No lens
              </button>
              {lenses.map((l) => (
                <button
                  key={l.slug}
                  type="button"
                  onClick={() => setLens(l.slug === lens ? "" : l.slug)}
                  className={`h-6 px-2.5 rounded-full text-[11px] font-medium border transition-all shrink-0 ${
                    lens === l.slug
                      ? "bg-[var(--crimson)] text-[var(--paper)] border-[var(--crimson)]"
                      : "bg-[var(--paper)] text-[var(--ink-2)] border-[var(--hairline)] hover:border-[var(--crimson)] hover:text-[var(--crimson)]"
                  }`}
                  data-testid={`share-lens-${l.slug}`}
                >
                  {l.name}
                </button>
              ))}
            </div>
            {lens && (
              <p className="mt-1.5 font-sans text-[11.5px] text-[var(--crimson)]" data-testid="share-lens-hint">
                Readers will see what each development means for {lensName}.
              </p>
            )}
          </div>}

          {/* URL + copy */}
          <div className="flex items-center gap-2">
            <div
              className="flex-1 min-w-0 h-9 px-3 rounded-[8px] border border-[var(--hairline)] bg-[var(--surface-2)] flex items-center font-mono text-[10.5px] text-[var(--ink-2)]"
              style={{ overflow: "hidden" }}
              data-testid="share-url-display"
            >
              <span className="truncate block w-full">{shareUrl}</span>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              style={{ flexShrink: 0 }}
              className={`h-9 px-4 rounded-[8px] inline-flex items-center gap-1.5 text-[12.5px] font-medium transition-colors ${
                copied
                  ? "bg-[var(--crimson)] text-[var(--paper)]"
                  : "bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--crimson)]"
              }`}
              data-testid="share-copy-button"
            >
              {copied ? <Check size={13} strokeWidth={2} /> : <Copy size={13} strokeWidth={1.75} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>

          {/* Native share — only shown where API is available */}
          {typeof navigator.share === "function" && (
            <button
              type="button"
              data-testid="share-native-button"
              onClick={async () => {
                const used = await nativeShare({ title: shareText, url: shareUrl });
                if (used) onClose();
              }}
              className="w-full h-11 rounded-[8px] border border-[var(--crimson)] text-[var(--crimson)] flex items-center justify-center gap-2 text-[13px] font-medium hover:bg-[var(--crimson)] hover:text-[var(--paper)] transition-colors"
            >
              <Share2 size={14} strokeWidth={1.75} />
              Share via…
            </button>
          )}

          {/* Share networks */}
          <div className="grid grid-cols-4 gap-2">
            {networks.map((n) => (
              <a
                key={n.id}
                href={n.href}
                target="_blank"
                rel="noopener noreferrer"
                className="h-11 rounded-[8px] border border-[var(--hairline)] flex flex-col items-center justify-center gap-1 text-[var(--ink-2)] hover:border-[var(--crimson)] hover:text-[var(--crimson)] transition-colors"
                data-testid={`share-network-${n.id}`}
              >
                <n.icon size={14} strokeWidth={1.75} />
                <span className="font-mono text-[8.5px] tracking-[0.1em] uppercase">{n.label}</span>
              </a>
            ))}
          </div>

          <p className="font-mono text-[9px] tracking-[0.06em] text-[var(--muted)]">
            {scope === "story"
              ? "Link opens the story with a rich preview on all platforms."
              : `Archived at midnight UTC — this link always shows the ${todayKey()} edition.`}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareBriefDialog;
